<script lang="ts" setup>
import { useBreakpoints, useScroll, useSwipe } from '@vueuse/core';
import type { SpotBySlug } from '~~/app/composables/useSpots';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useAsyncData,
  useAuth,
  useDialog,
  useI18n,
  useMetrics,
  useSupabaseClient,
  useToast,
  watch,
} from '#imports';

const supabase = useSupabaseClient();
const { showError } = useToast();
const { t } = useI18n();
const { currentUser } = useAuth();
const { openDialog } = useDialog();

const breakpoints = useBreakpoints({mobile: 920}, {ssrWidth: 920});
const isMobile = breakpoints.smaller('mobile');

const props = defineProps<{
  spot: SpotBySlug;
}>();

const emit = defineEmits([
  'close',
  'pauseTimer',
  'resumeTimer',
  'resetTimer',
  'next',
  'prev',
  'subscribe',
]);

const isSubscriber = computed(() => currentUser.value?.hasActiveSubscription);
const supportsThisCreator = computed(() =>
  currentUser.value?.supports?.some(
    (support) => support.creator_id === props.spot.map_slug.profiles.id,
  ),
);
const isCreator = computed(
  () => currentUser.value?.profile?.id === props.spot.map_slug.profiles.id,
);

const {
  data: similarSpots,
  status: similarStatus,
  refresh: similarRefresh
} = await useAsyncData('similarspots', async () => {
  if (!props.spot) return
  if (!props.spot.categories?.id) return
  const { data, error } = await supabase
    .from('spots')
    .select(`
      *,
      map_id!inner(
        user_id
      )
    `)
    .neq('map_id.user_id', props.spot.map_slug.profiles.id)
    .eq('category_id', props.spot.categories.id)
    .limit(10);


  if (error) showError(t('messages.maps.fetchSpots'));
  return data;
})

const {
  data: creatorSpots,
  status: creatorSpotsStatus,
  refresh: creatorSpotsRefresh
} = await useAsyncData('creatorspots', async () => {

  if (!props.spot) return
  const { data, error } = await supabase
    .from('spots')
    .select(`
      *,
      map_id!inner(
        user_id
      )
    `)
    .eq('map_id.user_id', props.spot.map_slug.profiles.id as string)
    .limit(10);


  if (error) showError(t('messages.maps.fetchSpots'));
  return data;
})

// Navigate prev/next with left/right arrow keys
const handleKeyup = (event: KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    emit('prev');
  } else if (event.key === 'ArrowRight') {
    emit('next');
  }
};

onMounted(() => {
  window.addEventListener('keyup', handleKeyup);
});

onUnmounted(() => {
  window.removeEventListener('keyup', handleKeyup);
});

const handleClose = () => {
  emit('close');
};

const googleMapsUrl = computed(() => {
  if (props.spot?.google_place_id) {
    return `https://www.google.com/maps/search/?api=1&query=${props.spot.lat},${props.spot.lon}&query_place_id=${props.spot.google_place_id}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${props.spot.lat},${props.spot.lon}`;
});

const bookSpotUrl = computed(() => {
  // probably implement the event tracking here?
  if (props.spot?.booking_link) {
    return props.spot.booking_link;
  }
  if (props.spot?.website) {
    return props.spot.website;
  }
  return googleMapsUrl.value;
});

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const el = ref<HTMLElement | null>(null);
const { y } = useScroll(el);

watch(y, () => {
  if (y.value > 0) {
    emit('pauseTimer');
  } else if (y.value === 0) {
    emit('resumeTimer');
  }
});

const { trackEvent } = useMetrics();
const handleBookingIntent = (url: string) => {
  if (props.spot) {
    trackEvent('booking_intent', {
      map_id: props.spot.map_id,
      creator_id: props.spot.map_slug.profiles.id,
      spot_id: props.spot.id || '',
      spot_name: props.spot.name || '',
      spot_destination: props.spot.cities?.name || '',
      spot_category: props.spot.categories?.name || '',
    });
    openInNewTab(url);
  }
};

const handleOpenMaps = (url: string) => {
  if (props.spot) {
    trackEvent('open_spot_in_google_maps', {
      map_id: props.spot.map_id,
      creator_id: props.spot.map_slug.profiles.id,
      spot_id: props.spot.id || '',
      spot_name: props.spot.name || '',
      spot_destination: props.spot.cities?.name || '',
      spot_category: props.spot.categories?.name || '',
    });
  }
  openInNewTab(url);
};

const spotHeader = ref(null);
const { isSwiping, direction } = useSwipe(spotHeader);

watch(isSwiping, () => {
  if (direction.value === 'right') {
    emit('prev');
  } else if (direction.value === 'left') {
    emit('next');
  }
});

const handleCoverTouch = (e) => {
  if (e.changedTouches[0].clientY > (window.innerHeight / 6) * 5) return;
  if (e.changedTouches[0].clientX > (window.innerWidth / 6) * 5) {
    emit('next');
  } else if (e.changedTouches[0].clientX < window.innerWidth / 6) {
    emit('prev');
  }
};

onMounted(() => {
  if (props.spot) {
    trackEvent('viewed_spot', {
      creator_id: props.spot.map_slug.profiles.id,
      creator_handle: props.spot.map_slug.profiles.mappr_handle  || '',
      spot_id: props.spot.id,
      spot_name: props.spot.name as string  || '',
      spot_destination: props.spot.cities?.name  || '',
      spot_category: props.spot.categories?.name  || '',
      map_name: props.spot.map_slug.title  || '',
      map_id: props.spot.map_slug.id,
    });
  }
});

const showPreviewOverlay = computed(() => {
  if (!props.spot) return false;
  if (isCreator.value) return false;

  const visibilityChecks = {
    authenticated: !currentUser.value,
    subscriber: !isSubscriber.value,
    supporter: !supportsThisCreator.value,
  };

  return visibilityChecks[props.spot.visibility] || false;
});

const determineVisibilityType = () => {
  if (!currentUser.value) return 'public';
  if (currentUser.value && !isSubscriber.value && !supportsThisCreator.value)
    return 'authenticated';
  if (currentUser.value && isSubscriber.value && !supportsThisCreator.value)
    return 'subscribed';
  if (currentUser.value && supportsThisCreator.value) return 'supporter';
  return null;
};

const openShareDialog = () => {
  if (!props.spot) return;
  try {
    openDialog('MPShare', {
      map: props.spot,
      subtitle: 'Share spot:',
      version: 'spot',
    });
  } catch (e) {
    showError('Error opening share dialog');
  }
};

const handleCopyAddress = () => {
  if (!props.spot) return;
  try {
    navigator.clipboard.writeText(props.spot.formatted_address);
  } catch (e) {
    showError('Error copying address');
  }
};

</script>
<template>
<Teleport to="body" :disabled="!isMobile">
  <div ref="el" class="spot" @touchstart="emit('pauseTimer')" @touchend="y === 0 ? emit('resumeTimer') : null">
    <div v-if="spot" class="spot-container animate-zoom-pan fadeInOut">
      <div ref="spotHeader" class="spot-header" v-if="!showPreviewOverlay">
        <div class="close-spot-button" @click="handleClose">
          <i class="ri-close-line close-icon" />
        </div>
      </div>
      <div class="spot-info background" :style="`background-image: url(${spot.cover_image_url})`">
        <div class="spot-info-wrappers" @touchend="handleCoverTouch">

          <div class="spot-info-mobile" v-if="!showPreviewOverlay">
            <small v-if="spot.cities"> {{ spot.cities.country }} -  {{ spot.cities.name }} </small>
            <h1>{{ spot.name }}</h1>
            <p v-if="spot.summary">
              {{ spot.summary.substring(0, 80) }}{{ spot.summary.length > 80 ? '...' : '' }}
            </p>
            <div v-if="props.spot.map_slug.profiles" class="spot-creator">
              <MPImg :src="props.spot.map_slug.profiles.profile_image_url"/>
              <span><small>Selected by</small> @{{ props.spot.map_slug.profiles.mappr_handle }}</span>
            </div>
            <!-- <MPCopyRightText
              class="mobile-bookmark"
              v-if="spot.cover_image_copyright"
              :copyright-text="spot.cover_image_copyright"
            /> -->
          </div>
        </div>
      </div>
      <section class="spot-details" v-if="!showPreviewOverlay">
        <div class="spot-details-content">
          <div class="mobile-summary">
            <hr class="mobile-trigger" />
            <div class="mobile-spot-title">
              <small v-if="spot.cities"> {{ spot.cities.country }} -  {{ spot.cities.name }} </small>
              <h3>{{ spot.name }}</h3>
            </div>

          </div>
          <div class="spot-action-buttons">
            <MPButton
              v-if="bookSpotUrl"
              label="Book Now"
              version="primary"
              :size="isMobile ? 'small' : 'medium'"
              rounded
              class="book-spot-button"
              @click="handleBookingIntent(bookSpotUrl)"
            />
            <MPButton
              label="Open Map"
              rounded
              :size="isMobile ? 'small' : 'medium'"
              class="open-maps-button"
              @click="handleOpenMaps(googleMapsUrl)"
            />
            <MPButton
              hide-label
              icon-only
              label="Share Spot"
              :size="isMobile ? 'small' : 'medium'"
              rounded
              icon="ri-share-line"
              version="secondary"
              class="share-spot-button"
              @click="openShareDialog"
            />
            <MPBookmark
              v-if="spot.id"
              :spot-id="spot.id"
              :size="isMobile ? 'small' : 'medium'"
              background-color="var(--color-slate-100)"
              icon-color="var(--color-rose-500)"
              link
              tooltip
              class="desktop-bookmark"
              bookmark="spot_bookmarks"
            />
          </div>
          <div class="spot-address">
            <hr />
            <div class="address-container" v-if="spot.formatted_address">
              <p v-if="spot.formatted_address">
                <span>Address:</span> {{ spot.formatted_address }}
              </p>
              <i class="ri-file-copy-line" @click="handleCopyAddress" />
            </div>
            <hr />
          </div>

          <!-- <div class="image-gallery"></div> -->
          <article class="summary" v-if="spot.summary">
            {{spot.summary}}
          </article>

          <article>
            <ul class="spot-tidbids">
              <li v-if="spot.known_for" class="spot-known-for">
                <i class="ri-bookmark-3-line" />
                <p>
                  <span>Known for:</span><br> {{ spot.known_for }}
                </p>

              </li>
              <li v-if="spot.why_go" class="spot-why-go">
                <i class="ri-question-line" />
                <p>
                  <span>Why go:</span><br> {{ spot.why_go }}
                </p>

              </li>
              <li v-if="spot.vibe" class="spot-vibe">
                <i class="ri-empathize-line" />
                <p>
                  <span>Vibe:</span><br> {{ spot.vibe }}
                </p>
              </li>
              <li v-if="spot.transport" class="spot-transport">
                <i class="ri-train-line" />
                <p>
                  <span>Transport:</span><br> {{ spot.transport }}
                </p>
              </li>
              <li v-if="spot.tips" class="spot-tips">
                <i class="ri-flag-line" />
                <p>
                  <span>Tips:</span><br> {{ spot.tips }}
                </p>
              </li>
              <li v-if="spot.website" class="spot-website">
                <i class="ri-global-line" />
                <p>
                  <span>Website: </span><br>
                  <a class="spot-website-link" @click="openInNewTab(spot.website)"> {{ spot.website.slice(0, 25) + (spot.website.length > 25 ? '...' : '') }}</a>
                </p>
              </li>
            </ul>
          </article>
          <article class="belongs-to-map">
            <h5>Featured in:</h5>
            <div class="map-card-container">
              <MPMapCard :map="spot.map_slug" />
            </div>

          </article>
          <article class="other-spots-by-creator" v-if="creatorSpots && creatorSpots.length > 5">
            <h5>Other spots by {{ spot.map_slug.profiles.mappr_handle }}</h5>
            <MPViewportSpill show-controls class="similar-spot-list">
              <MPSpotCard v-for="spot in creatorSpots" :spot="spot" show-tier />
            </MPViewportSpill>
          </article>

          <article class="similar-spots" v-if="similarSpots && similarSpots.length > 5">
            <h5>Similar Spots</h5>
            <MPViewportSpill show-controls class="similar-spot-list">
              <MPSpotCard v-for="spot in similarSpots" :spot="spot" show-tier />
            </MPViewportSpill>
          </article>
          <article class="about-creator">
            <div class="creator-image">
              <MPImg :src="spot.map_slug.profiles.profile_image_url as string" :width="800" />
              <div class="creator-image-overlay-container">
                <div>
                  <p>About</p>
                  <p>@{{ spot.map_slug.profiles.mappr_handle }}</p>
                </div>
                <MPFollow :creator="spot.map_slug.profiles.id as string" />
              </div>
            </div>
            <article>
              <p class="about-bio-text">{{ spot.map_slug.profiles.bio_text }}</p>
              <NuxtLinkLocale :to="`/@${spot.map_slug.profiles.mappr_handle}`" class="about-goto-button">View Profile</NuxtLinkLocale>
            </article>
          </article>
        </div>
      </section>
      </div>
      <div v-if="showPreviewOverlay" class="preview-overlay">
        <MPSpotTierOverlay :visibility="determineVisibilityType()" :creator="spot.map_slug.profiles" @subscribe="emit('subscribe', $event)" @close="handleClose"  />
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="postcss">
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/typography.pcss";
@import '@/assets/css/layouts.pcss';
@import '@/assets/css/scrollbar.pcss';

$gridGap:1rem;
@keyframes fadeInOut {
  0% {
    opacity: 0;
    background-size: auto 120%
  }
  2% {
    opacity: 1;
  }
  100% {
    background-size: auto 100%
  }
}

.fadeInOut {
  animation: fadeInOut 20s ease-in-out;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
  border-radius: 1rem;

  padding: 1.5rem 1rem 1rem;
  @mixin screen xs {
    padding: 1rem;
  }
}

.spot {
  z-index: 9000;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  @mixin scrollbar-reset;
  width: 100vw;
  min-height: 100vh;
  max-height:100%;
  text-align: center;
  color: var(--color-white);
  scrollbar-width: none;

  @mixin screen xs {
    z-index: 50;
    max-width: 100%;
    position: relative;
    max-height: 100%;
    height: fit-content;
    background-color: var(--main-background-color);
    border-radius: 0.75rem;
    border: 1px solid var(--color-gray-100);
  }

  &::-webkit-scrollbar {
    display: none;
  }
}

.spot .spot-container {
  position: relative;
}

.spot .spot-container .spot-header {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 99;
  display: flex;

  @mixin screen xs {
    position: absolute;

  }
  .close-spot-button {
    color: white;
    position: absolute;
    top: 0.5rem;
    right: 1rem;
    z-index: 1000;
    cursor: pointer;
    i {
      font-size: 1.5rem;
    }
  }
}

.spot .spot-container .spot-info {
  z-index: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10;

  @mixin screen xs {
    width: auto;
  }
}

.spot .spot-container .background {
  min-height: 93vh;
  max-height: 93vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;


  @mixin screen xs {
    min-height: 18rem;
    max-height: 20rem;
    width: 100%;
    position: relative;
  }

}

.spot .spot-container .spot-details {
  z-index: 10;
  text-align: center;
  position: relative;



  .spot-details-content {
    margin-top: 0;
    border-radius: 1rem 1rem 0 0;
    background-color: var(--main-background-color);
    color: var(--color-black);
    text-align: left;
    h1, .category {
      text-align: center;
    }
    @mixin screen xs {
      border-radius: 0;
    }
  }
  p {
    text-align: left;
  }
}

.spot .spot-details .spot-action-buttons {
  position: relative;
  display: flex;
  gap: 0.5rem;
  padding-inline: 1rem;
  margin-top: 1rem;

  @mixin screen md {
    padding-inline: 1.5rem;
  }
}

.spot-address {
  padding-inline: 1rem;
  color: var(--color-slate-500);

  @mixin screen md {
    padding-inline: 1.5rem;
  }
  span {
    font-weight: bold;
  }
}

.address-container {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  align-items: center;

  i {
    font-size: 1.5rem;
    cursor: pointer;
  }
}

.spot .spot-container .spot-info-wrappers {
  display: grid;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.80) 20%, rgba(0, 0, 0, 0.60) 30%, rgba(0, 0, 0, 0.20) 50%, rgba(0, 0, 0, 1) 100%);

  .spot-info-mobile {
    margin-top: 2rem;
    h1 {
      margin-top: 0.5rem;
      padding-inline: 3rem;
    }
    p {
      padding-inline: 3rem;
    }
    small {
      color: var(--color-rose-400);
    }
  }

  @mixin screen xs {
    width: 100%;
    height: 100%;
    text-align: left;

    background: linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 56.73%, rgba(0, 0, 0, 0.5) 100%);

    text-align: center;
    h1 {
      margin-top: 2rem;
      @mixin typeface-h2;
    }
  }
}

.spot-tidbids {
  list-style: none;
  padding-block: 2rem;
  padding-inline: 2rem;

  @mixin screen md {
    padding-inline: 4rem;
  }

  li {
    display: flex;
    gap: 1rem;

    i {
      margin-top: 1rem;
      font-size: 1.5rem;
    }
  }

  .spot-known-for i {
    color: var(--color-rose-200);
  }

  .spot-why-go i {
    color: var(--color-indigo-400);
  }

  .spot-vibe i {
    color: var(--color-green-200);
  }

  .spot-transport i {
    color: var(--color-yellow-400)
  }

  .spot-tips i {
    color: var(--color-orange-400)
  }

  .spot-website i {
    color: var(--color-slate-400)
  }
}

.belongs-to-map {
  background-color: var(--color-slate-50);
  padding-block: 1rem;
  h5 {
    padding-inline: 1rem;
    margin-bottom: 1rem;
    color: var(--color-slate-600);
    @mixin screen md {
      padding-inline: 1.5rem;
    }

  }
}

.belongs-to-map .map-card-container {
  padding-inline: 1rem;
  @mixin screen md {
    padding-inline: 1.5rem;
  }
}

.other-spots-by-creator {
  padding-left: 1rem;
  padding-top: 2rem;
  padding-bottom: 1rem;
  h3 {
    margin-bottom: 1rem;
  }
}
.similar-spots {
  padding-left: 1rem;
  padding-top: 1rem;
  padding-bottom: 2rem;
  h3 {
    margin-bottom: 1rem;
  }
}

.similar-spot-list {
  width: 100%;
  gap: $gridGap;
  &:deep(.spot) {
    width: calc(($layoutWide / 5) - $gridGap);

  }
}

.about-creator {

  .creator-image {
    position: relative;
    width: 100%;

    img {
      border-radius: 1rem 1rem 0 0;

      @mixin screen xs {
        border-radius: 0;
      }
    }

    .creator-image-overlay-container {
      position: absolute;
      border-radius: 1rem 1rem 0 0;
      inset: 0;
      display: flex;
      justify-content: space-between;
      background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 60%, rgba(0, 0, 0, 0.9));
      place-items: end;
      padding: 2rem;

      @mixin screen xs {
        border-radius: 0;
      }

      p {
        margin: 0;
        color: white;
      }
    }
  }

  article {
    padding-inline: 4rem;
    display: grid;
  }

  .about-bio-text {
    position: relative;
    overflow: hidden;
    display: -webkit-box;
    line-clamp: 5;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
  }

  .about-bio-text::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(to bottom, transparent 0%, var(--main-background-color) 80%);
    pointer-events: none;
  }

  .about-goto-button {
    position: relative;
    top: -1rem;
    place-self: center;
  }
}

.spot .spot-container h1 {
  font-size: 2.5rem;
  font-style: normal;
  font-weight: 700;
  line-height: 140%;
  margin: 0 0 1rem;
}

.spot .spot-container p {
  @mixin typeface-regular;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  margin-top: 0.5rem;
}

.preview-overlay + .spot-header + .spot-info {
  position: absolute;
  top: 0;
  z-index: -1;
}

.spot-details .summary {
  @mixin typeface-summary;
  background-color: var(--color-indigo-50);
  color: var(--color-slate-800);
  border: 1px solid var(--color-slate-200);

  padding: 2rem;
  text-align: center;
  font-style: italic;
}

.mobile-summary {
  display: grid;
  place-items: center;

  hr {
    border: 4px solid var(--color-slate-400);
    border-radius: 1rem;
    width: 4rem;
    margin-bottom: 2rem;
  }

  .mobile-spot-title {
    place-self: start;
    padding-inline: 1rem;

    h3 {
      margin-top: 0.5rem;
    }

    small {
      color: var(--color-rose-500);
    }
  }


  @mixin screen xs {
    display: none;
  }
}
.spot-creator {
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 0.5rem 1rem 1rem;
  display: flex;

  backdrop-filter: blur(1px);
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: var(--color-white);
  span {
    @mixin typeface-regular;
    line-height: 1.1;
  }
  small {
    display: block;
    color: var(--color-slate-300);
    font-size: 0.9em;
  }
  img {
    object-fit: cover;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 100%;
  }
}

</style>
